import { Spinner } from "@repo/ui/components/Base/Spinner";
import { DataTable } from "@repo/ui/components/Base/DataTable";
import { layoutDefaults, useColumns } from "./UsersDataTableColumns";
import { EditUserSheet } from "./EditUserSheet";
import type { UserWRole } from "@repo/types";
import { useEffect, useState } from "react";
import { DeleteConfirmation } from "./DeleteConfirmation";
import { useUserColumns } from "@/hooks/useUserColumns";
import { KEYS } from "@/lib/constants";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import { tenantRepo } from "@/repositories/tenantRepo";

export const UsersTable = ({
  tenantId,
  editUser,
  resetEditUser,
}: {
  tenantId: string;
  editUser: UserWRole | null;
  resetEditUser?: () => void;
}) => {
  const { data: users } = useUserList(tenantId);

  const handleEditUser = (user: UserWRole) => {
    setEditSheetOpen(true);
    setActiveUser(user);
  };

  useEffect(() => {
    if (editUser && resetEditUser) {
      handleEditUser(editUser);
      resetEditUser();
    }
  });

  const [activeUser, setActiveUser] = useState<UserWRole>();
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    KEYS.USER_TABLE_COLUMNS,
    layoutDefaults
  );

  const handleEdit = (id: string) => {
    setEditSheetOpen(true);
    setActiveUser(users?.find((user) => user.id === id));
  };

  const handleDelete = (id: string) => {
    setConfirmRemoveOpen(true);
    setActiveUser(users?.find((user) => user.id === id));
  };

  const confirmDelete = async () => {
    if (activeUser) {
      await tenantRepo.removeUser(tenantId, activeUser.id);
    }
  };

  return (
    <>
      <DeleteConfirmation
        onConfirm={() => confirmDelete()}
        open={confirmRemoveOpen}
        setOpen={setConfirmRemoveOpen}
      />
      {!users && <Spinner />}
      <EditUserSheet
        open={editSheetOpen}
        setOpen={setEditSheetOpen}
        tenantId={tenantId}
        user={activeUser ?? ({} as UserWRole)}
      />

      <DataTable
        columns={useColumns({
          handleEdit,
          handleDelete,
        })}
        data={users ?? []}
        filterColumn={{ label: "first name", columnName: "firstName" }}
        userDefaults={defaultState}
        factoryDefaults={layoutDefaults}
        onLayoutSave={saveTableLayout}
        onLayoutReset={resetTableLayout}
      />
    </>
  );
};
